/**
 * The theme on screen. The Worker already set <html data-theme/data-contrast/data-motion> and the custom theme's
 * <style id="theme-custom"> before first paint (appearance.ts); this keeps them current: saves from Settings →
 * Appearance, "Match my device" following prefers-color-scheme live, and "Reduce motion" (user setting or OS).
 * Charts re-read their colors when `version` changes (useThemeVersion).
 */
import { create } from "zustand";
import { DEFAULT_APPEARANCE, cleanAppearance, htmlAttributes, shownTheme, type Appearance } from "./appearance";
import { customThemeCss } from "./themes";

interface AppearanceState { appearance: Appearance; version: number }
export const appearanceStore = create<AppearanceState>(() => ({ appearance: DEFAULT_APPEARANCE, version: 0 }));
export const useAppearance = () => appearanceStore((s) => s.appearance);
/** Changes whenever the colors on screen change (theme, contrast, device dark mode). */
export const useThemeVersion = () => appearanceStore((s) => s.version);

const media = (q: string) => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(q) : null);
const darkQuery = media("(prefers-color-scheme: dark)");
const osReducedQuery = media("(prefers-reduced-motion: reduce)");

/** "Reduce motion" on while the OS doesn't ask for it: the stylesheets' reduced-motion rules, applied unconditionally. */
function userReducedMotion(on: boolean) {
  let el = document.getElementById("user-reduced-motion") as HTMLStyleElement | null;
  if (!on || osReducedQuery?.matches) { el?.remove(); return; }
  if (el) return;
  const rules: string[] = [];
  const walk = (list: CSSRuleList) => {
    for (const r of Array.from(list)) {
      if (r instanceof CSSMediaRule && /prefers-reduced-motion:\s*reduce/.test(r.media.mediaText)) rules.push(...Array.from(r.cssRules, (x) => x.cssText));
      else if (r instanceof CSSMediaRule || r instanceof CSSSupportsRule) walk(r.cssRules);
    }
  };
  for (const sheet of Array.from(document.styleSheets)) { try { walk(sheet.cssRules); } catch { /* another origin's sheet (fonts) */ } }
  el = document.createElement("style");
  el.id = "user-reduced-motion";
  el.textContent = rules.join("\n");
  document.head.appendChild(el);
}

/** Colors cross-fade when the theme changes (not on first paint, not with reduced motion). */
function crossFade() {
  const d = document.documentElement;
  if (d.hasAttribute("data-motion") || osReducedQuery?.matches) return;
  d.classList.add("theme-switching");
  window.setTimeout(() => d.classList.remove("theme-switching"), 320);
}

function apply(a: Appearance, animate: boolean) {
  const d = document.documentElement;
  const attrs = htmlAttributes(a);
  const nextTheme = shownTheme(a, !!darkQuery?.matches);
  const changed = d.getAttribute("data-theme") !== nextTheme || d.hasAttribute("data-contrast") !== a.highContrast;
  if (animate && changed) crossFade();
  for (const k of ["data-match-device", "data-contrast", "data-motion"]) if (!(k in attrs)) d.removeAttribute(k);
  for (const [k, v] of Object.entries(attrs)) d.setAttribute(k, v);
  d.setAttribute("data-theme", nextTheme);
  let style = document.getElementById("theme-custom") as HTMLStyleElement | null;
  if (!style) { style = document.createElement("style"); style.id = "theme-custom"; document.head.appendChild(style); }
  const css = a.custom ? customThemeCss(a.custom) : "";
  if (style.textContent !== css) style.textContent = css;
  userReducedMotion(a.reduceMotion);
  appearanceStore.setState((s) => ({ appearance: a, version: s.version + 1 }));
}

let started = false;
/** Boot with the appearance from snapshot:me (the Worker's copy of what it put in the HTML). */
export function initAppearance(saved: unknown) {
  apply(cleanAppearance(saved), false);
  if (started) return;
  started = true;
  darkQuery?.addEventListener("change", () => { if (appearanceStore.getState().appearance.matchDevice) apply(appearanceStore.getState().appearance, true); });
  osReducedQuery?.addEventListener("change", () => apply(appearanceStore.getState().appearance, false));
}

/** Show a change at once and save it; puts the old one back if the save fails. */
export async function saveAppearance(patch: Partial<Appearance>): Promise<boolean> {
  const prev = appearanceStore.getState().appearance;
  const next = cleanAppearance(patch, prev);
  apply(next, true);
  const ok = await fetch("/api/dashboard/settings/appearance", {
    method: "PUT", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(patch),
  }).then((r) => r.ok).catch(() => false);
  if (!ok) apply(prev, true);
  return ok;
}
